import { StorageService as IStorageService } from '../types/ServiceTypes';

/**
 * StorageService - localStorage abstraction service
 * Handles all localStorage operations with error handling
 */
export class StorageService implements IStorageService {
  /**
   * Store data in localStorage with JSON serialization
   */
  set<T>(key: string, value: T): void {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error(`Failed to store data for key "${key}":`, error);
      throw new Error(`Storage operation failed for key "${key}"`);
    }
  }

  /**
   * Retrieve data from localStorage with JSON deserialization
   */
  get<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return null;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Failed to retrieve data for key "${key}":`, error);
      // Return null for corrupted data rather than throwing
      return null;
    }
  }

  /**
   * Remove data from localStorage
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove data for key "${key}":`, error);
      throw new Error(`Storage removal failed for key "${key}"`);
    }
  }

  /**
   * Clear all data from localStorage
   */
  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
      throw new Error('Storage clear operation failed');
    }
  }

  /**
   * Check if localStorage is available
   */
  isAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check if a key exists in localStorage
   */
  exists(key: string): boolean {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error(`Failed to check existence of key "${key}":`, error);
      return false;
    }
  }
}
